import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import OrderBasketItem from '../basket/order-basket-item';
import {
  DeliveryMode,
  ERROR_CODES,
  ORDER_METHODS,
  PAYMENT_METHODS,
  PaymentMethod,
  PaymentMethodBrand,
  SCREENS,
} from '../../services/exports/Constants';
import { useSelector } from 'react-redux';
import useScreenType from '../../hooks/utility/useScreenType';
import DeliveryThreshold from '../delivery/DeliveryThreshold';
import OrderValidationManager from '../../services/api/OrderValidationManager';
import CheckoutThreshold from '../checkout/CheckoutThreshold';
import RemovedItemsNotice from './RemovedItemsNotice';
import { isPaymentMethodHiddenForOrderMethod } from '../../utils/payment-options-visibility';
import { Trans, useTranslation } from 'react-i18next';
import useInitialData from '../../hooks/global/useInitialData';
import useRouter from '../../hooks/navigation/useRouter';
import useBasket from '../../hooks/basket/useBasket';
import { StoreInterface } from '../../store/types';
import EmptyBasketIllustration from '../../assets/illustrations/EmptyBasketIllustration';
import useAppMethods from '../../hooks/utility/useAppMethods';
import moment from 'moment';
import classnames from 'classnames';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import { CompleteBasketItem } from '../../providers/BasketProvider';
import { ReservedHttpResponseCodes, ValidationErrorResponseData } from '../../services/exports/Types';
import collect from 'collect.js';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useIsCheckoutDisabled from '../../hooks/basket/useIsCheckoutDisabled';
import RecommendedProducts from '../../modules/mobile/features/shared/components/recommended-products/RecommendedProducts';
import useMenu from '../../hooks/menu/useMenu';
import CheckoutButton from '../buttons/CheckoutButton';
import useAnalytics from '../../hooks/analytics/useAnalytics';
import useCompanyHours from '../../hooks/availability/useCompanyHours';
import { CompanyDetailsInterface, PaymentOptionPropertiesInterface } from '../../services/exports/Interfaces';
import useAuth from '../../hooks/global/useAuth';
import TrustLabel from '../labels/TrustLabel';
import CashbackProgressSection from '../cashback/CashbackProgressSection';
import DiscountDisplacementNotice from '../../features/discount-exclusivity/DiscountDisplacementNotice';
import useCashbackProgram from '../../hooks/cashback/useCashbackProgram';
import usePaymentFlow from '../../hooks/payment/usePaymentFlow';
import PaymentMethodBrandIcons from '../payment/payment-methods/PaymentMethodBrandIcons';
import AmericanExpressIcon from '../../assets/icons/payment-methods/AmericanExpressIcon';
import VisaIcon from '../../assets/icons/payment-methods/VisaIcon';
import MasterCardIcon from '../../assets/icons/payment-methods/MasterCardIcon';

interface Props {
  className?: string;
  loading?: boolean;
  checkoutDisabled?: boolean;
  onBasketItemUpdateClick?: (index: number, item: CompleteBasketItem) => void;
  onCheckoutClick?: () => void;
}

const OrderBasket = (props: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderBasketScreen' });

  const { className, onBasketItemUpdateClick, onCheckoutClick, loading, checkoutDisabled } = props;

  const { data, refresh } = useInitialData();
  const { company, franchise } = data;
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { open } = useCompanyHours();
  const { isDesktop, isMobile } = useScreenType();
  const { navigate } = useRouter();
  const { isMobileApp } = useMobileApp();
  const { trackBeginCheckout } = useAnalytics();
  const { data: orderData } = useOrderFlow();

  const { isOrderMethodInfoComplete, getOrderData, update, resetOrder } = useOrderFlow();
  const { items, refreshBasket, basketItemsCount, total, subtotal } = useBasket();
  const { toggleOrderMethodModal, toggleMenuItemModal, toggleOrderBasketModal, toggleLoginModal } = useAppMethods();
  const { isLoggedIn } = useAuth();
  const { hasCashbackProgram, calculateExpectedCashback, balance } = useCashbackProgram();
  const { getUpsellModifierGroup } = useMenu();
  const upsellModifierGroup = getUpsellModifierGroup(items);
  const hasUpsellModifierGroup = !!upsellModifierGroup?.modifiers?.length;

  const [validating, setValidating] = useState(false);

  // A room/table remembered from an earlier order may belong to another outlet of the franchise or be
  // gone since; only one this restaurant still offers counts, so the method modal opens instead of a 422.
  const hasKnownRoomServiceLocation = (company?.room_service_locations ?? []).some(
    (location) => location.id === cached_order?.room_service_location_id,
  );

  const orderMethodInfoComplete =
    isOrderMethodInfoComplete &&
    (cached_order?.method === ORDER_METHODS.PICKUP ||
      (cached_order?.method === ORDER_METHODS.ROOM_SERVICE && hasKnownRoomServiceLocation) ||
      (cached_order?.method === ORDER_METHODS.DELIVERY &&
        company?.delivery_mode === DeliveryMode.Internal &&
        !!cached_order?.delivery_zone?.id) ||
      (cached_order?.method === ORDER_METHODS.DELIVERY &&
        company?.delivery_mode === DeliveryMode.External &&
        !!cached_order?.delivery_zone?.id &&
        !!cached_order?.delivery_quote?.id));

  useEffect(() => {
    if (!isDesktop) window.scrollTo(0, 0);
    refreshBasket();
  }, []);

  async function validateBeforeProceeding(): Promise<void> {
    setValidating(true);
    if (!(await isValid())) {
      return setValidating(false);
    }

    setValidating(false);

    navigate({
      web: {
        to: SCREENS.CHECKOUT,
      },
    });
    trackBeginCheckout(orderData, items);
    toggleOrderBasketModal({ openModal: false });
  }

  async function isValid(): Promise<boolean> {
    if (!orderMethodInfoComplete && franchise?.has_multiple_companies) {
      toggleOrderMethodModal({ openModal: true });

      return false;
    }

    if (
      cached_order.method === ORDER_METHODS.ROOM_SERVICE &&
      franchise?.has_multiple_companies &&
      ((!company?.table_number_hidden ? !cached_order.room_service_details : false) || !hasKnownRoomServiceLocation)
    ) {
      toggleOrderMethodModal({ openModal: true });

      return false;
    }

    if (cached_order?.scheduled_for && moment(cached_order?.scheduled_for).isBefore(moment())) {
      handleOrderTimeNotAvailable();

      return false;
    }

    const ignoredValidationExceptions: string[] = collect()
      .when(!open, (collection) =>
        collection.merge([
          ERROR_CODES.PICKUP_CLOSED,
          ERROR_CODES.DELIVERY_CLOSED,
          ERROR_CODES.ORDER_TIME_NOT_AVAILABLE,
          ERROR_CODES.ITEMS_MENU_TIME_UNAVAILABLE,
        ]),
      )
      .toArray();

    const onValidationError = async (response: ValidationErrorResponseData): Promise<boolean> => {
      if (collect(response.rules).has('customer_phone_number')) {
        update('customer_phone_number', null);

        const { success } = await OrderValidationManager.validate(
          getOrderData({
            customer_phone_number: null,
            ignored_error_codes: ignoredValidationExceptions,
          }),
          {
            [ERROR_CODES.ORDER_TIME_NOT_AVAILABLE]: handleOrderTimeNotAvailable,
            [ERROR_CODES.ORDER_SLOT_IS_TAKEN]: handleOrderTimeNotAvailable,
            [ERROR_CODES.ORDERS_PAUSED]: handleOrderTimeNotAvailable,
            [ERROR_CODES.PICKUP_CLOSED]: handleOrderTimeNotAvailable,
            [ERROR_CODES.DELIVERY_CLOSED]: handleOrderTimeNotAvailable,
            [ERROR_CODES.DELIVERY_UNAVAILABLE]: handleOrderTimeNotAvailable,
            [ERROR_CODES.ITEMS_OUT_OF_STOCK]: handleItemsOutOfStock,
            [ERROR_CODES.ADDRESS_OUTSIDE_DELIVERY_ZONE]: handleAddressOutsideDeliveryArea,
          },
        );

        if (!success) {
          resetOrder({ customer_phone_number: null });

          return false;
        }

        return success;
      }

      return true;
    };

    const { success, response, status } = await OrderValidationManager.validate(
      getOrderData({ ignored_error_codes: ignoredValidationExceptions }),
      {
        [ERROR_CODES.ORDER_TIME_NOT_AVAILABLE]: handleOrderTimeNotAvailable,
        [ERROR_CODES.ORDER_SLOT_IS_TAKEN]: handleOrderTimeNotAvailable,
        [ERROR_CODES.ORDERS_PAUSED]: handleOrderTimeNotAvailable,
        [ERROR_CODES.PICKUP_CLOSED]: handleOrderTimeNotAvailable,
        [ERROR_CODES.DELIVERY_CLOSED]: handleOrderTimeNotAvailable,
        [ERROR_CODES.DELIVERY_UNAVAILABLE]: handleOrderTimeNotAvailable,
        [ERROR_CODES.DELIVERY_QUOTE_ALREADY_USED]: () => toggleOrderMethodModal({ openModal: true }),
        [ERROR_CODES.ITEMS_OUT_OF_STOCK]: handleItemsOutOfStock,
        [ERROR_CODES.ADDRESS_OUTSIDE_DELIVERY_ZONE]: handleAddressOutsideDeliveryArea,
      },
    );

    if (status === ReservedHttpResponseCodes.ValidationError) {
      return onValidationError(response.response.data);
    }

    return success;
  }

  function handleOrderTimeNotAvailable(): void {
    navigate({
      web: {
        to: SCREENS.HOME_SCREEN,
      },
    });
    toggleOrderMethodModal({ openModal: true });
  }

  function handleItemsOutOfStock(): Promise<void> | void {
    return refresh().then(() =>
      navigate({
        web: {
          to: SCREENS.HOME_SCREEN,
        },
      }),
    );
  }

  function handleAddressOutsideDeliveryArea(): void {
    navigate({
      web: {
        to: SCREENS.HOME_SCREEN,
      },
    });
    toggleOrderMethodModal({ openModal: true });
  }

  const isCheckoutDisabled = useIsCheckoutDisabled() || collect(items).count() === 0 || checkoutDisabled;

  const renderEmptyBasket = (): ReactElement => {
    return (
      <div className="grow w-full flex flex-col justify-center items-center z-1">
        <EmptyBasketIllustration />
        <p className="text-center text-sm text-muted-foreground">
          <Trans t={t}>empty_order_basket.description</Trans>
        </p>
      </div>
    );
  };

  const onBasketItemUpdate = (index: number, item: CompleteBasketItem): void => {
    if (onBasketItemUpdateClick) {
      return onBasketItemUpdateClick(index, item);
    }

    toggleMenuItemModal({
      openModal: true,
      product: item.entry,
      lineItem: item,
      basketItemIndex: index,
    });
  };

  const expectedCashback = calculateExpectedCashback(total);

  return (
    <div className={classnames('h-full flex flex-col relative overflow-hidden', className)}>
      <div className="overflow-y-auto scrollbar-hide h-full">
        <div className="p-md lg:pb-[220px] pb-[240px] space-y-md">
          <TrustLabel className="mb-sm" />
          {!isLoggedIn && (
            <p className="text-center text-xs text-muted-foreground">
              {t('already_member')}{' '}
              <button
                onClick={() => toggleLoginModal({ openModal: true, onCloseRedirect: () => null })}
                className="text-brand-primary font-semibold underline"
                data-test="basket-sign-in"
              >
                {t('sign_in')}
              </button>
            </p>
          )}
          <CashbackProgressSection />
          <RemovedItemsNotice />
          {items.length > 0 ? (
            <div className="space-y-md">
              {/* Machine-readable basket totals for e2e — currency-agnostic, always present with items */}
              <span data-test="basket-summary" data-total={total} data-subtotal={subtotal} className="hidden" />
              <DiscountDisplacementNotice />
              <DeliveryThreshold />
              {items.map((item: CompleteBasketItem, index) => (
                <OrderBasketItem
                  key={`basket-item-${index}`}
                  coupon_type={cached_order?.coupon_type}
                  data={item}
                  index={index}
                  editable
                  onUpdateClick={() => onBasketItemUpdate(index, item)}
                />
              ))}
              {hasUpsellModifierGroup && (
                <RecommendedProducts className="-mx-md mt-md px-md py-lg bg-brand-primary/30">
                  <RecommendedProducts.Header
                    title={t('upsell.title')}
                    className={classnames({ '!text-base': isMobile })}
                  />
                  <RecommendedProducts.Content products={upsellModifierGroup.modifiers} />
                </RecommendedProducts>
              )}
              <CheckoutThreshold />
            </div>
          ) : (
            renderEmptyBasket()
          )}
        </div>
      </div>
      {!isMobileApp && (
        <div
          className={classnames(
            'fixed bottom-0 flex flex-col left-0 bg-brand-card right-0 border-t-xs border-brand-border',
            {
              absolute: isDesktop,
            },
          )}
        >
          <CheckoutButton
            title={t('buttons.checkout')}
            onClick={onCheckoutClick ?? validateBeforeProceeding}
            hideAddProductsButton
            loading={validating || loading}
            disabled={isCheckoutDisabled}
            className="border-none"
          />
          {hasCashbackProgram && basketItemsCount > 0 && (
            <p className="text-center text-xs text-muted-foreground pb-xs">
              {isLoggedIn
                ? t('footer.member_earning', { points: expectedCashback, balance })
                : t('footer.guest_earning')}
            </p>
          )}
          <OrderBasket.PaymentOptions company={company} basketItemsCount={basketItemsCount} />
        </div>
      )}
    </div>
  );
};

export default OrderBasket;

OrderBasket.PaymentOptions = ({
  company,
  basketItemsCount,
}: {
  company: CompanyDetailsInterface;
  basketItemsCount: number;
}) => {
  const { enabledWallets } = usePaymentFlow();
  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const isPaymentOptionSupported = useCallback(
    (paymentOption: PaymentOptionPropertiesInterface) => {
      return (
        company?.supported_payment_methods?.includes(paymentOption.payment_method_id) &&
        (paymentOption.payment_method_id !== PaymentMethod.ApplePay ||
          enabledWallets.includes(PaymentMethod.ApplePay)) &&
        (paymentOption.payment_method_id !== PaymentMethod.GooglePay ||
          enabledWallets.includes(PaymentMethod.GooglePay)) &&
        !isPaymentMethodHiddenForOrderMethod(
          company?.hidden_payment_methods,
          paymentOption.payment_method_id,
          cached_order?.method,
        )
      );
    },
    [enabledWallets, cached_order?.method, company?.hidden_payment_methods, company?.supported_payment_methods],
  );

  const paymentOptions = useMemo<PaymentOptionPropertiesInterface[]>(() => {
    return collect(PAYMENT_METHODS())
      .reject((item: PaymentOptionPropertiesInterface) => !isPaymentOptionSupported(item))
      .toArray();
  }, [isPaymentOptionSupported, company?.hidden_payment_methods, company?.supported_payment_methods]);

  const PaymentOptionIcon = ({
    key,
    paymentOption,
  }: {
    key: string;
    paymentOption: PaymentOptionPropertiesInterface;
    children?: ReactElement;
  }) => {
    return (
      <div
        key={key}
        className={classnames(
          'border-xs h-[30px] w-[40px] rounded-[6px] px-[6px] bg-card relative flex items-center justify-center',
          {
            'w-[40px] !p-none bg-[#FEB4C7]': paymentOption.payment_method_id === PaymentMethod.Sofort,
          },
        )}
      >
        <PaymentMethodBrandIcons
          {...paymentOption}
          className={classnames({
            'w-full h-full': paymentOption.payment_method_id === PaymentMethod.Sofort,
          })}
        />
      </div>
    );
  };

  const CardPaymentOptions = () => {
    const icons = [
      {
        icon: <VisaIcon />,
        brand: PaymentMethodBrand.Visa,
      },
      {
        icon: <MasterCardIcon />,
        brand: PaymentMethodBrand.MasterCard,
      },
      {
        icon: <AmericanExpressIcon type={2} />,
        brand: PaymentMethodBrand.AmericanExpress,
      },
    ];

    return icons.map((item, index) => {
      return (
        <div
          key={index}
          className={classnames(
            'border-xs h-[30px] w-[40px] rounded-[6px] px-[6px] bg-card relative flex items-center justify-center overflow-hidden',
            {
              '!px-[3px] bg-[#1F72CD]': item.brand === PaymentMethodBrand.AmericanExpress,
            },
          )}
        >
          {item.icon}
        </div>
      );
    });
  };

  const PaymentOption = ({ paymentOption }: { paymentOption: PaymentOptionPropertiesInterface }) => {
    switch (paymentOption.payment_method_id) {
      case PaymentMethod.Card:
        return <CardPaymentOptions />;
      default:
        return <PaymentOptionIcon paymentOption={paymentOption} key={paymentOption.id.toString()} />;
    }
  };

  return (
    basketItemsCount > 0 && (
      <div className="w-full flex flex-row flex-wrap justify-center items-center gap-sm pb-sm px-sm">
        {paymentOptions?.map((paymentOption) => (
          <PaymentOption key={paymentOption!.payment_method_id!.toString()} paymentOption={paymentOption} />
        ))}
      </div>
    )
  );
};
